import { Icon } from 'semantic-ui-react';
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { StripMessageWrapper, DropdownWrapper, NavbarWrapper } from './styled';
import Button from '../../../../r/components/shared/button';
import useActivePage from '../../../../r/hooks/active-page';
import { files } from '../../../libs/assets';
import win from '../../../../r/libs/win';

const applicablePages = ['Features'];

const navigation = [
  {
    text: 'Solution',
    target: '_self',
    dropdown: [
      {
        items: [
          { href: '/survey-forms', text: 'Online Surveys' },
          { href: '/data-collection', text: 'Data Collection' },
          { href: '/registration-forms', text: 'Registration Form' },
          { href: '/application-forms', text: 'Application Form' },
          { href: '/e-signature-forms', text: 'E-Signature' },
          { href: '/booking-forms', text: 'Booking/Reservations' }
        ]
      },
      {
        items: [
          { href: '/payment-forms', text: 'Payment Forms' },
          { href: '/order-forms', text: 'Order Forms' },
          { href: '/offline-forms', text: 'Offline Forms' },
          { href: '/contact-forms', text: 'Contact Forms' },
          { href: '/sign-up-forms', text: 'Sign-Up Forms' }
        ]
      }
    ]
  },
  { href: '/templates', text: 'Templates', target: '_self' },
  {
    href: '/e/features',
    text: 'Features',
    target: '_self',
    dropdown: [
      {
        title: 'Security',
        items: [
          { href: '/e/features/gdpr-compliance', text: 'GDPR Compliant' }, 
          { href: '/e/features/private-forms', text: 'Private Forms' },
          { href: '/e/features/pci-compliance', text: 'PCI Complaint' },
          { href: '/e/features/custom-sub-domain', text: 'Subdomain' },
          { href: '/e/features/secure-forms', text: 'Secure Form' }
        ]
      },
      {
        title: 'Form Options',
        items: [
          { href: '/e/features/electronic-signatures', text: 'Digital signature' },
          { href: '/e/features/mobile-forms', text: 'Mobile Forms' },
          { href: '/e/features/geolocation', text: 'Location Enabled Forms' },
          { href: '/e/features#form-customisation', text: 'Customization' }
        ]
      },
      {
        title: 'Integrations',
        items: [
          { href: 'https://help.formpl.us/s/article/change-response-storage-option', text: 'Google Sheet Integrations', target: '_blank' },
          { href: '/e/features/payment-processor', text: 'Payment' },
          { href: 'https://help.formpl.us/s/article/how-to-change-the-default-file-storage-provider', text: 'Cloud Storage', target: '_blank' },
          { href: 'https://help.formpl.us/s/article/zapier-integration', text: 'Zapier Integration', target: '_blank' }
        ]
      },
      {
        title: 'Teams & Analytics',
        items: [
          { href: '/e/features/teams', text: 'Multiple users and Teams' },
          { href: '/e/features/analytics', text: 'Form Analytics' },
          { href: '/e/features/data-import-export', text: 'Data Import & Export' }
        ]
      },
      {
        title: 'Sharing Options',
        items: [
          { href: '/e/features/embedded-form', text: 'Embeddable Forms' },
          { href: '/e/features/custom-form-url', text: 'Custom Form Link' },
          { href: '/e/features/wordpress-embed', text: 'Wordpress Form Embed' }
        ]
      },
      {
        title: 'Notifications',
        items: [
          { href: '/e/features/email-invitation', text: 'Email Invitation' },
          { href: '/e/features/autoresponder', text: 'Confirmation Emails' },
          { href: '/e/features/email-notifications', text: 'Notify Teammates' },
          { href: '/e/features/periodic-notifications', text: 'Periodic Notification' },
          { href: '/e/features/document-merge', text: 'Form2Doc' }
        ]
      }
    ]
  },
  {
    text: 'Resources',
    target: '_self',
    dropdown: [
      {
        items: [
          { href: '/blog', content: 'Information, how-to and guides to data collection and surveys.', text: 'Blog' },
          { href: 'https://help.formpl.us', content: 'All the help you need on using Formplus', text: 'Support' }
        ]
      }
    ]
  },
  { href: '/pricing', target: '_self', text: 'Pricing' }
];

const StripMessage = ({ text_color, content, link = '', color, cta }) => {
  const style = { color: text_color, backgroundColor: color };

  return (
    <StripMessageWrapper style={style} title="Click here to view details">
      <div className="message">{content}</div>
      {link && (
        <Button
          tone="primary"
          size="tiny"
          href={link}
          borderless
          text={cta}
        />
      )}
    </StripMessageWrapper>
  );
};

const SubMenu = ({ list, title: { target, text, href } }) => {
  return (
    <DropdownWrapper className="dropdown">
      <div className="title">
        {href ? (
          <Button
            fontFamily="Quicksand"
            target={target}
            href={href}
            text={text}
            borderless
          />
        ) : (
          text
        )}
        
        <Icon className="icon chevron down" />
      </div>

      <div className={`menu ${text.toLowerCase()}`}>
        {list.map(({ title, items }, dropdownIndex) => (
          <div className="item" key={dropdownIndex}>
            {title && <h3>{title}</h3>}
            {items.map(({ target = '_self', content, href, text }) => (
              <div className="sub-item" key={text}>
                <Button
                  fontFamily="Quicksand"
                  target={target}
                  href={href}
                  text={text}
                  borderless
                  wrapText
                  compact
                />

                {content && <p>{content}</p>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </DropdownWrapper>
  );
};

// If you are working on a page (e.g.: /data-collection page) where the navbar requires
// any other color than white, just add the color against the page pathname.
const navbarThemeColors = {
  pathname: 'color'
};

const Navbar = () => {
  const navbarTheme = navbarThemeColors[location.pathname] ?? 'white';
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const { stripMessage, isLoggedIn } = win;
  const activePage = useActivePage();

  const handleMobileMenuToggle = () => setOpenMobileMenu(!openMobileMenu);

  if (!applicablePages.includes(activePage.name)) return null;

  return (
    <>
      {stripMessage && <StripMessage {...stripMessage} />}

      <NavbarWrapper style={{ '--theme-color': navbarTheme }}>
        <div className="link navbar-toggler" onClick={handleMobileMenuToggle}>
          <button aria-label="Toggle navigation" data-toggle="collapse" type="button">
            <svg width="24" height="24" clipRule="evenodd" fillRule="evenodd" strokeLinejoin="round" strokeMiterlimit="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="m22 16.75c0-.414-.336-.75-.75-.75h-18.5c-.414 0-.75.336-.75.75s.336.75.75.75h18.5c.414 0 .75-.336.75-.75zm0-5c0-.414-.336-.75-.75-.75h-18.5c-.414 0-.75.336-.75.75s.336.75.75.75h18.5c.414 0 .75-.336.75-.75zm0-5c0-.414-.336-.75-.75-.75h-18.5c-.414 0-.75.336-.75.75s.336.75.75.75h18.5c.414 0 .75-.336.75-.75z" fillRule="nonzero"/>
            </svg>
          </button>
        </div>

        <div className="link brand">
          <a href="/">
            <img src={files.logo} alt="FormPlus Logo" />
          </a>
        </div>

        <nav className={`${openMobileMenu ? 'expand' : ''} right`}>
          <div className="link">
            <a href="/">Home</a>
          </div>

          {navigation.map(({ dropdown, target, href = '', text }) => (
            <div className="link" key={text}>
              {!href ? (
                <SubMenu title={{ target, text, href }} list={dropdown} />
              ) : (
                !dropdown ? (
                  <Button
                    fontFamily="Quicksand"
                    target={target}
                    href={href}
                    text={text}
                    borderless
                    compact
                  />
                ) : (
                  <SubMenu title={{ target, href, text }} list={dropdown} />
                )
              )}
            </div>
          ))}

          <div className="auth">
            {!isLoggedIn ? (
              <>
                <Button
                  fontFamily="Quicksand"
                  href="/e/auth"
                  text="Log in"
                  borderless
                  shadow
                />

                <Button
                  href="/e/auth/sign-up"
                  fontFamily="Quicksand"
                  tone="primary"
                  text="Sign up"
                  borderless
                  shadow
                />
              </>
            ) : (
              <Button
                fontFamily="Quicksand"
                text="Dashboard"
                borderless
                href="/r"
                shadow
              />
            )}
          </div>
        </nav>

        <nav className="right mobile">
          <div className="auth">
            <Button
              text={!isLoggedIn ? 'Log in' : 'Dashboard'}
              href={!isLoggedIn ? '/e/auth' : '/r'}
              fontFamily="Quicksand"
              borderless
              shadow
            />
          </div>
        </nav>
      </NavbarWrapper>
    </>
  );
};

SubMenu.propTypes = {
  title: PropTypes.shape({
    target: PropTypes.oneOf(['_parent', '_blank', '_self', '_top']),
    text: PropTypes.string,
    href: PropTypes.string,
  }).isRequired,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          content: PropTypes.string,
          text: PropTypes.string,
          href: PropTypes.string,
        })
      )
    })
  ).isRequired
};

export default Navbar;
